import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { nanoid } from 'nanoid';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { MdAdd, MdDelete } from 'react-icons/md';
import * as yup from 'yup';

import FormInput from '@/components/form/FormInput';
import FormSwitch from '@/components/form/FormSwitch';

import { SurveyField, SurveyFieldOption, SurveyFieldType } from '../types';

type SurveyFieldFormValues = {
  id?: string;
  label: string;
  type: SurveyFieldType;
  isRequired: boolean;
  options: SurveyFieldOption[];
  maxRate: number | null;
};

type SurveyFieldFormProps = {
  initialValue?: SurveyField;
  onSubmit: (field: SurveyField) => void;
  onCancel?: () => void;
};

const fieldTypes: Array<{ value: SurveyFieldType; label: string }> = [
  { value: 'textField', label: 'Pole tekstowe' },
  { value: 'textArea', label: 'Obszar tekstowy' },
  { value: 'radio', label: 'Wybór pojedynczy' },
  { value: 'checkbox', label: 'Wybór wielokrotny' },
  { value: 'scale', label: 'Skala ocen' },
  { value: 'divider', label: 'Separator' },
];

const optionSchema = yup.object({
  id: yup.string().required(),
  label: yup.string().trim().required('Etykieta opcji jest wymagana'),
});

const fieldSchema = yup.object({
  id: yup.string().optional(),
  label: yup.string().trim().required('Etykieta pola jest wymagana'),
  type: yup
    .mixed<SurveyFieldType>()
    .oneOf(fieldTypes.map((item) => item.value))
    .required('Wybierz typ pola'),
  isRequired: yup.boolean().required(),
  options: yup
    .array()
    .of(optionSchema)
    .default([])
    .test(
      'options-required',
      'Dodaj co najmniej jedną opcję',
      function (this: yup.TestContext, options: SurveyFieldOption[] | undefined) {
        const type = (this.parent?.type as SurveyFieldType | undefined) ?? 'textField';
        if (type === 'radio' || type === 'checkbox') {
          return Array.isArray(options) && options.length > 0;
        }
        return true;
      }
    ),
  maxRate: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : Number(originalValue)
    )
    .nullable()
    .when('type', {
      is: 'scale',
      then: (schema) =>
        schema
          .typeError('Podaj liczbę całkowitą')
          .required('Określ maksymalną wartość skali')
          .integer('Wartość musi być liczbą całkowitą')
          .min(1, 'Minimalna wartość to 1'),
      otherwise: (schema) => schema.transform(() => null).nullable(true),
    }),
});

const emptyValues: SurveyFieldFormValues = {
  label: '',
  type: 'textField',
  isRequired: false,
  options: [],
  maxRate: null,
};

const likertScaleOptions = [
  'Całkowicie się nie zgadzam',
  'Raczej się nie zgadzam',
  'Nie mam zdania',
  'Raczej się zgadzam',
  'Całkowicie się zgadzam',
];

export const SurveyFieldForm = ({ initialValue, onSubmit, onCancel }: SurveyFieldFormProps) => {
  const defaultValues = useMemo(() => {
    if (!initialValue) {
      return emptyValues;
    }

    return {
      id: initialValue.id,
      label: initialValue.label,
      type: initialValue.type,
      isRequired: initialValue.isRequired,
      options: initialValue.options ?? [],
      maxRate: initialValue.maxRate ?? null,
    };
  }, [initialValue]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SurveyFieldFormValues>({
    resolver: yupResolver(fieldSchema),
    defaultValues,
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'options',
  });

  const type = watch('type');
  const optionErrors = Array.isArray(errors.options) ? errors.options : [];
  const optionsErrorMessage =
    !Array.isArray(errors.options) && errors.options?.message
      ? (errors.options.message as string)
      : undefined;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (type === 'radio' || type === 'checkbox') {
      if (fields.length === 0) {
        append({ id: nanoid(), label: '' });
      }
    } else if (fields.length > 0) {
      replace([]);
    }

    if (type === 'scale') {
      const currentMaxRate = getValues('maxRate');
      setValue('maxRate', currentMaxRate ?? 5);
    } else {
      setValue('maxRate', null);
    }

    if (type === 'divider') {
      setValue('isRequired', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleAddOption = () => {
    append({ id: nanoid(), label: '' });
  };

  const handleRemoveOption = (index: number) => {
    remove(index);
  };

  const handleApplyLikertScale = () => {
    replace(
      likertScaleOptions.map((label) => ({
        id: nanoid(),
        label,
      }))
    );
  };

  const onSubmitForm = handleSubmit((values) => {
    const field: SurveyField = {
      id: values.id || nanoid(),
      label: values.label.trim(),
      isRequired: values.isRequired,
      type: values.type,
      options:
        values.type === 'radio' || values.type === 'checkbox'
          ? values.options.map((option) => ({
              id: option.id || nanoid(),
              label: option.label.trim(),
            }))
          : undefined,
      maxRate: values.type === 'scale' && values.maxRate ? Number(values.maxRate) : undefined,
    };

    onSubmit(field);
    if (!initialValue) {
      reset(emptyValues);
    }
  });

  return (
    <form onSubmit={onSubmitForm}>
      <Stack spacing={4}>
        <FormInput
          label="Etykieta pola"
          placeholder="Np. Jak oceniasz naszą placówkę?"
          {...register('label')}
          error={errors.label}
        />
        <FormControl isInvalid={!!errors.type}>
          <FormLabel>Typ pola</FormLabel>
          <Select {...register('type')}>
            {fieldTypes.map((fieldType) => (
              <option key={fieldType.value} value={fieldType.value}>
                {fieldType.label}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
        </FormControl>
        <FormSwitch
          control={control}
          name="isRequired"
          label="Pole wymagane"
          isDisabled={type === 'divider'}
        />

        {type === 'radio' ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            alignSelf="flex-start"
            onClick={handleApplyLikertScale}
          >
            Dodaj odpowiedzi skali Likerta
          </Button>
        ) : null}

        {type === 'scale' ? (
          <FormInput
            type="number"
            label="Maksymalna wartość skali"
            min={1}
            {...register('maxRate')}
            error={errors.maxRate}
          />
        ) : null}

        {type === 'radio' || type === 'checkbox' ? (
          <Stack spacing={3}>
            <Text fontWeight="semibold">Opcje odpowiedzi</Text>
            {fields.map((fieldItem, index) => (
              <Stack key={fieldItem.id} spacing={2}>
                <input
                  type="hidden"
                  {...register(`options.${index}.id` as const)}
                  defaultValue={fieldItem.id}
                />
                <FormControl isInvalid={!!optionErrors[index]?.label}>
                  <HStack justify="space-between" align="center" mb={1}>
                    <FormLabel htmlFor={`option-${fieldItem.id}`} m={0}>
                      Opcja {index + 1}
                    </FormLabel>
                    <IconButton
                      aria-label="Usuń opcję"
                      icon={<MdDelete />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleRemoveOption(index)}
                    />
                  </HStack>
                  <Input
                    id={`option-${fieldItem.id}`}
                    placeholder="Wprowadź etykietę opcji"
                    {...register(`options.${index}.label` as const)}
                  />
                  <FormErrorMessage>{optionErrors[index]?.label?.message}</FormErrorMessage>
                </FormControl>
              </Stack>
            ))}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              leftIcon={<MdAdd />}
              alignSelf="flex-start"
              onClick={handleAddOption}
            >
              Dodaj opcję
            </Button>
            {optionsErrorMessage ? (
              <Text color="red.500" fontSize="sm">
                {optionsErrorMessage}
              </Text>
            ) : null}
          </Stack>
        ) : null}

        <HStack justify="flex-end" spacing={2}>
          {onCancel ? (
            <Button type="button" variant="ghost" onClick={onCancel}>
              Anuluj
            </Button>
          ) : null}
          <Button type="submit" colorScheme="purple" isLoading={isSubmitting}>
            {initialValue ? 'Zapisz pole' : 'Dodaj pole'}
          </Button>
        </HStack>
      </Stack>
    </form>
  );
};
